import type { CVData } from "../types";

export function CVTemplateModern({ data }: { data: CVData }) {
  const { profile, experiences, education, skills, certificates } = data;

  return (
    <div
      className="cv-content mx-auto max-w-[800px] bg-white p-10 text-gray-800 print:p-8"
      style={{
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
        fontSize: 14,
        lineHeight: 1.6,
      }}
    >
      <h1 className="text-3xl font-bold" style={{ color: "#0f3460" }}>
        {profile.name}
      </h1>
      <p className="mt-0.5 text-sm text-gray-500">{profile.title}</p>
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
        {profile.email && <span>📧 {profile.email}</span>}
        {profile.phone && <span>📱 {profile.phone}</span>}
        {profile.address && <span>📍 {profile.address}</span>}
        {profile.github_url && <span>🔗 GitHub</span>}
        {profile.linkedin_url && <span>🔗 LinkedIn</span>}
      </div>

      {profile.bio && (
        <section className="mt-5">
          <h2
            className="mb-2 border-b-2 pb-1 text-base font-semibold uppercase tracking-wider"
            style={{ color: "#16213e", borderColor: "#0f3460" }}
          >
            Summary
          </h2>
          <p className="text-[13px]">{profile.bio}</p>
        </section>
      )}

      {experiences.length > 0 && (
        <section className="mt-5">
          <h2
            className="mb-2 border-b-2 pb-1 text-base font-semibold uppercase tracking-wider"
            style={{ color: "#16213e", borderColor: "#0f3460" }}
          >
            Work Experience
          </h2>
          <div className="space-y-3">
            {experiences.map((exp) => (
              <div key={exp.id}>
                <div className="flex items-baseline justify-between">
                  <span className="text-[15px] font-semibold">{exp.position}</span>
                  <span className="whitespace-nowrap text-xs text-gray-500">
                    {exp.period_start} – {exp.period_end || "Present"}
                  </span>
                </div>
                <div className="text-[13px]" style={{ color: "#0f3460" }}>
                  {exp.company}
                </div>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {exp.responsibilities.map((r, i) => (
                    <li key={i} className="text-[13px]">
                      {r}
                    </li>
                  ))}
                </ul>
                <div className="mt-1 flex flex-wrap gap-1">
                  {exp.tech_stack.map((t) => (
                    <span
                      key={t}
                      className="rounded px-1.5 py-px text-[11px]"
                      style={{ background: "#e8eaf6", color: "#283593" }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {education.length > 0 && (
        <section className="mt-5">
          <h2
            className="mb-2 border-b-2 pb-1 text-base font-semibold uppercase tracking-wider"
            style={{ color: "#16213e", borderColor: "#0f3460" }}
          >
            Education
          </h2>
          {education.map((edu) => (
            <div key={edu.id} className="mb-2">
              <div className="flex items-baseline justify-between">
                <span className="text-[15px] font-semibold">{edu.school}</span>
                <span className="text-xs text-gray-500">{edu.period}</span>
              </div>
              <div className="text-[13px]" style={{ color: "#0f3460" }}>
                {edu.degree}
                {edu.grade ? ` · ${edu.grade}` : ""}
              </div>
            </div>
          ))}
        </section>
      )}

      {skills.length > 0 && (
        <section className="mt-5">
          <h2
            className="mb-2 border-b-2 pb-1 text-base font-semibold uppercase tracking-wider"
            style={{ color: "#16213e", borderColor: "#0f3460" }}
          >
            Skills
          </h2>
          <div className="grid grid-cols-2 gap-1.5">
            {skills.map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-[13px]">
                <span className="w-36">{s.name}</span>
                <div className="h-1.5 flex-1 rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${s.level}%`, background: "#0f3460" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {certificates.length > 0 && (
        <section className="mt-5">
          <h2
            className="mb-2 border-b-2 pb-1 text-base font-semibold uppercase tracking-wider"
            style={{ color: "#16213e", borderColor: "#0f3460" }}
          >
            Certificates
          </h2>
          {certificates.map((c) => (
            <div key={c.id} className="mb-1 text-[13px]">
              <strong>{c.name}</strong>
              {c.description && ` — ${c.description}`}
              {c.date && ` (${c.date})`}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
